import { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { api } from '../services/api';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { LoadingPanel, ErrorNotice, EmptyState } from './ui/States';
import { errorMessage, openBlankTab, showPrintable } from '../lib/printable';

interface Session {
  key: string;
  date: string;
  title: string;
  minutes: number | null;
  how: 'live' | 'recording';
}

interface Letter {
  sessions: Session[];
  included: string[];
  html: string;
}

const day = (v: string) =>
  new Date(v).toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

/** Pick a member's attended webinars and open the bilingual letter to print or save as PDF. */
export default function AttendanceLetter({
  userId,
  name,
  onClose,
}: {
  userId: string;
  name: string;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const list = useQuery<Letter>(
    ['attendance-letter', userId],
    async () => (await api.get(`/webinars/attendance-letter/${userId}`)).data.data,
  );
  useEffect(() => {
    if (list.data) setChosen(new Set(list.data.sessions.map((s) => s.key)));
  }, [list.data]);

  const open = useMutation(
    async () =>
      (await api.get(`/webinars/attendance-letter/${userId}`, { params: { only: [...chosen].join(',') } })).data
        .data as Letter,
  );
  const openLetter = () => {
    const tab = openBlankTab();
    open.mutate(undefined, { onSuccess: (d) => showPrintable(tab, d.html), onError: () => tab?.close() });
  };
  const sessions = list.data?.sessions ?? [];
  const toggle = (key: string) => {
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setChosen(next);
  };

  return (
    <Modal
      title="Attendance letter"
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button disabled={!chosen.size || open.isLoading} onClick={openLetter}>
            {open.isLoading ? 'Preparing…' : 'Open printable letter'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      <p className="text-small text-ink-2">
        A letter in English and French listing the webinars {name} attended. It opens in a new tab: print it or save it
        as PDF from the browser. Nothing is emailed.
      </p>
      {list.isLoading ? <LoadingPanel label="Loading attendance…" height={100} /> : null}
      {list.isError ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(list.error, 'Could not load attendance.')}</ErrorNotice>
        </div>
      ) : null}
      {open.isError ? (
        <div className="mt-3">
          <ErrorNotice>{errorMessage(open.error, 'Could not prepare the letter.')}</ErrorNotice>
        </div>
      ) : null}
      {list.data && !sessions.length ? (
        <EmptyState title="No webinar attendance">
          No Zoom attendance, session survey or recording view is recorded for this member.
        </EmptyState>
      ) : null}
      {sessions.length ? (
        <table className="w-full text-small mt-4">
          <thead>
            <tr className="text-left text-ink-2">
              {['', 'Date', 'Webinar', 'Attendance'].map((h) => (
                <th key={h} className="px-2 py-1 font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => (
              <tr key={s.key} className="border-t border-line align-top">
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    checked={chosen.has(s.key)}
                    onChange={() => toggle(s.key)}
                    aria-label={`Include ${s.title}`}
                  />
                </td>
                <td className="px-2 py-2 whitespace-nowrap tabular">{day(s.date)}</td>
                <td className="px-2 py-2">{s.title}</td>
                <td className="px-2 py-2 whitespace-nowrap text-ink-2">
                  {s.how === 'recording' ? 'Recording viewed' : s.minutes ? `Live, ${s.minutes} min` : 'Live'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </Modal>
  );
}
